import {
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'

export interface ScenarioEvalMetadata {
  simulation_mode?: 'smoke' | 'stress'
  scenario_type?: string
  capability_id?: string
  conversation_pattern_id?: string
  smoke_unit_kind?: string
  smoke_unit_description?: string
  expected_route_outcome?: {
    expected_intent_name?: string
    target_node_type?: string
  }
}

export interface PersistedScenario {
  uuid: string
  flow_id: number
  scenario: {
    id: string
    name?: string
    persona: {
      personality: string
      emotional_state?: string
      behavioral_traits?: string[]
      details?: Record<string, unknown>
    }
    goal: string
    language?: string
    tags: string[]
    world_state?: Record<string, unknown>
    start_node_params?: Record<string, unknown>
    interruption?: { enabled: boolean; probability: number }
    stt_noise?: { enabled: boolean; severity: string }
    non_answer?: { enabled: boolean; probability: number }
    eval_metadata?: ScenarioEvalMetadata
  }
  created_at: string | null
  updated_at: string | null
}

export interface ScenariosResponse {
  scenarios: PersistedScenario[]
  total: number
  page: number
  page_size: number
}

// --- Run history types ---

export interface SimulationRunScenario {
  uuid: string
  scenario_id: number | null
  scenario_name: string
  goal: string
  tags: string[]
  status: string
  stop_reason?: string
  turn_count?: number
  evaluation?: Record<string, unknown>
  eval_error?: boolean
  error?: string
}

export interface RToolCall {
  id?: string
  name?: string
  arguments?: string
  output?: unknown
  is_error?: boolean
  tool_type?: string
  mocked?: boolean
  mock_source_key?: string
}

export interface RResponseItem {
  type?: 'message' | 'tool_call'
  role?: string
  content?: string
  tool_call?: RToolCall
}

export interface RTranscriptTurn {
  turn: number
  user: string
  agent: string
  intent: string
  node_uuid: string
  variables: Record<string, unknown>
  variables_by_node?: Record<string, Record<string, unknown>>
  tool_calls?: RToolCall[]
  response_items?: RResponseItem[]
  is_interruption: boolean
  partial_assistant_msg: string
}

export type RScenarioData = PersistedScenario | PersistedScenario['scenario']

export interface SimulationRunScenarioDetail extends SimulationRunScenario {
  scenario_data?: RScenarioData
  transcript: RTranscriptTurn[]
}

export interface SimulationRunSummary {
  uuid: string
  flow_id: number
  name: string | null
  status: string
  scenario_count: number
  completed_count: number
  scenarios_passed: number
  scenarios_failed: number
  max_turns: number
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export interface SimulationRunDetail extends SimulationRunSummary {
  scenarios: SimulationRunScenario[]
}

interface RunsResponse {
  runs: SimulationRunSummary[]
  total: number
  page: number
  page_size: number
}

// --- Scenario queries ---

const SCENARIOS_PAGE_SIZE = 50

export { SCENARIOS_PAGE_SIZE }

export const useScenariosQuery = (phloUuid: string, page: number) =>
  useQuery({
    queryKey: [...flowQueryKeys().simulationScenarios(phloUuid), page],
    queryFn: async () => {
      const resp = await flowService.listSimulationScenarios(phloUuid, page, SCENARIOS_PAGE_SIZE)
      return (resp as { data: ScenariosResponse }).data
    },
    enabled: !!phloUuid,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

export const useDeleteScenarioMutation = (phloUuid: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      await flowService.deleteSimulationScenario(uuid)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationScenarios(phloUuid),
      })
    },
  })
}

export const useDeleteAllScenariosMutation = (phloUuid: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await flowService.deleteAllSimulationScenarios(phloUuid)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationScenarios(phloUuid),
      })
    },
  })
}

export const useRenameSimulationRunMutation = (phloUuid: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ runUuid, name }: { runUuid: string; name: string }) => {
      await flowService.renameSimulationRun(runUuid, name)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationRuns(phloUuid),
      })
    },
  })
}

// --- Run history queries ---

const RUNS_PAGE_SIZE = 20

export const useSimulationRunsQuery = (phloUuid: string) =>
  useInfiniteQuery({
    queryKey: [...flowQueryKeys().simulationRuns(phloUuid), 'infinite'],
    queryFn: async ({ pageParam }) => {
      const resp = await flowService.listSimulationRuns(phloUuid, pageParam, RUNS_PAGE_SIZE)
      return (resp as { data: RunsResponse }).data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.page_size < lastPage.total ? lastPage.page + 1 : undefined,
    enabled: !!phloUuid,
    staleTime: 10_000,
  })

export const useSimulationRunQuery = (runUuid: string | null) =>
  useQuery({
    queryKey: flowQueryKeys().simulationRun(runUuid ?? ''),
    queryFn: async () => {
      const resp = await flowService.getSimulationRun(runUuid as string)
      return (resp as { data: SimulationRunDetail }).data
    },
    enabled: !!runUuid,
    staleTime: 30_000,
  })

const SCENARIO_DETAIL_STALE_TIME = 60_000

export const useSimulationRunScenarioQuery = (
  runUuid: string | null,
  scenarioUuid: string | null,
) =>
  useQuery({
    queryKey: flowQueryKeys().simulationRunScenario(runUuid ?? '', scenarioUuid ?? ''),
    queryFn: async () => {
      const resp = await flowService.getSimulationRunScenario(
        runUuid as string,
        scenarioUuid as string,
      )
      return (resp as { data: SimulationRunScenarioDetail }).data
    },
    enabled: runUuid != null && scenarioUuid != null,
    staleTime: SCENARIO_DETAIL_STALE_TIME,
  })

export const prefetchSimulationRunScenario = (
  queryClient: QueryClient,
  runUuid: string,
  scenarioUuid: string,
) => {
  queryClient.prefetchQuery({
    queryKey: flowQueryKeys().simulationRunScenario(runUuid, scenarioUuid),
    queryFn: async () => {
      const resp = await flowService.getSimulationRunScenario(runUuid, scenarioUuid)
      return (resp as { data: SimulationRunScenarioDetail }).data
    },
    staleTime: SCENARIO_DETAIL_STALE_TIME,
  })
}
